import { Query } from 'react-query'
import { IChangeType, IPositionChange, IProjectVersionChanges } from 'src/generated-types/project-version-changes'
import { queryClient } from 'src/query-client'

type ChangesPatch = (positions: IPositionChange[]) => IPositionChange[]

// The positions are back to their published state, so they no longer differ from it. Removed positions are matched
// apart from the draft's, since one of those may have the removed one's name.
export const withoutChanges =
  (names: string[], removed = false): ChangesPatch =>
  positions =>
    positions.filter(p => !(names.includes(p.name) && (p.type === IChangeType.Removed) === removed))

// A position the draft added simply disappears; any other one becomes a removed one, under its published name.
export const withRemoved =
  (names: string[]): ChangesPatch =>
  positions => {
    const draftChanges = positions.filter(p => p.type !== IChangeType.Removed && names.includes(p.name))
    const removed = names
      .filter(name => !draftChanges.some(p => p.name === name && p.type === IChangeType.Added))
      .map(name => ({
        name: draftChanges.find(p => p.name === name)?.previousName ?? name,
        type: IChangeType.Removed,
        kinds: [IChangeType.Removed],
        fields: [],
        components: [],
        work: [],
      }))
    return [...positions.filter(p => !draftChanges.includes(p)), ...removed]
  }

// The server needs seconds to recompute a draft's changes, so an edit whose outcome is known shows it right away.
const patchCachedChanges = async (projectId: EntityId, patch: ChangesPatch) => {
  const predicate = (query: Query) =>
    query.state.data != null && String(query.queryKey[0] ?? '').endsWith(`/${projectId}/versions/changes`)
  // An answer already on its way predates the edit and would undo the patch.
  await queryClient.cancelQueries({ predicate })
  queryClient
    .getQueryCache()
    .findAll({ predicate })
    .forEach(query => {
      const data = query.state.data as IProjectVersionChanges
      queryClient.setQueryData(query.queryKey, { ...data, positions: patch(data.positions) })
    })
}

// Everything cached for a project (positions, components, drawings, changes) after an edit that touches many of them.
// Keys are either endpoint URLs or ['name', ...params as strings], so both forms are matched.
export const refreshProjectData = async (projectId: EntityId, patchChanges?: ChangesPatch) => {
  if (patchChanges) await patchCachedChanges(projectId, patchChanges)
  const urlPart = `projects/${projectId}/`
  const idPart = String(projectId)
  const keyPart = new RegExp(`projectId\\W{1,3}${projectId}\\b`)
  queryClient.invalidateQueries({
    predicate: query => {
      const parts = Array.isArray(query.queryKey) ? query.queryKey : [query.queryKey]
      if (parts.some(part => part === idPart || part === projectId)) return true
      const key = JSON.stringify(query.queryKey)
      return key.includes(urlPart) || keyPart.test(key) || key.includes('with-prices')
    },
  })
}
